import { cn } from "@/lib/utils";
import {
  Check, X, Trophy, Lightbulb, BarChart3, Home, Gem, KeyRound,
  UtensilsCrossed, Car, Ship, Search, ClipboardList, Coins, Lock,
  Zap, ShieldCheck, Smartphone, Phone, Ban, Video, AlertTriangle,
  PenLine, Landmark, Sparkles, Target, Flame, Calendar, Calculator,
  Star, Wine, Mountain, MapPin, Palette, CircleCheck, CircleX,
  LogOut, Fingerprint, Sun
} from "lucide-react";

/* ── Emoji → Lucide icon mapping ── */
const iconCn = "w-4 h-4 shrink-0";

const emojiToIcon: Record<string, React.ReactNode> = {
  "🏆": <Trophy className={iconCn} />,
  "💡": <Lightbulb className={iconCn} />,
  "📊": <BarChart3 className={iconCn} />,
  "🏠": <Home className={iconCn} />,
  "💎": <Gem className={iconCn} />,
  "🔑": <KeyRound className={iconCn} />,
  "👋": <LogOut className={iconCn} />,
  "🍽️": <UtensilsCrossed className={iconCn} />,
  "🚗": <Car className={iconCn} />,
  "⛵": <Ship className={iconCn} />,
  "🔍": <Search className={iconCn} />,
  "📋": <ClipboardList className={iconCn} />,
  "💰": <Coins className={iconCn} />,
  "🔒": <Lock className={iconCn} />,
  "⚡": <Zap className={iconCn} />,
  "🛡️": <ShieldCheck className={iconCn} />,
  "🛡": <ShieldCheck className={iconCn} />,
  "📱": <Smartphone className={iconCn} />,
  "🪪": <Fingerprint className={iconCn} />,
  "☎️": <Phone className={iconCn} />,
  "☎": <Phone className={iconCn} />,
  "🚫": <Ban className={iconCn} />,
  "✅": <CircleCheck className={iconCn} />,
  "❌": <CircleX className={iconCn} />,
  "📹": <Video className={iconCn} />,
  "⚠️": <AlertTriangle className={iconCn} />,
  "⚠": <AlertTriangle className={iconCn} />,
  "📝": <PenLine className={iconCn} />,
  "🏛️": <Landmark className={iconCn} />,
  "🏛": <Landmark className={iconCn} />,
  "✨": <Sparkles className={iconCn} />,
  "🎯": <Target className={iconCn} />,
  "🔥": <Flame className={iconCn} />,
  "📅": <Calendar className={iconCn} />,
  "🧮": <Calculator className={iconCn} />,
  "🌟": <Star className={iconCn} />,
  "🍷": <Wine className={iconCn} />,
  "🏞️": <Mountain className={iconCn} />,
  "🏞": <Mountain className={iconCn} />,
  "🎭": <Palette className={iconCn} />,
  "🗺️": <MapPin className={iconCn} />,
  "🗺": <MapPin className={iconCn} />,
  "🐕": <Sun className={iconCn} />,
  "🏖️": <Sun className={iconCn} />,
  "🏖": <Sun className={iconCn} />,
  "🏡": <Home className={iconCn} />,
  "💳": <Coins className={iconCn} />,
};

/** Resolve an emoji string to a Lucide icon, or fall back to the raw string */
function renderIcon(icon?: string): React.ReactNode {
  if (!icon) return null;
  const stripped = icon.replace(/\uFE0F/g, "");
  return emojiToIcon[icon] ?? emojiToIcon[stripped] ?? <span>{icon}</span>;
}

/** Yellow/gold highlight for key numbers or terms */
export function Highlight({ children, variant = "gold" }: { children: React.ReactNode; variant?: "gold" | "green" | "red" }) {
  const colors = {
    gold: "bg-gold/20 text-gold",
    green: "bg-emerald-500/15 text-emerald-400",
    red: "bg-red-500/15 text-red-400",
  };
  return (
    <span className={cn("px-1.5 py-0.5 rounded font-semibold", colors[variant])}>
      {children}
    </span>
  );
}

/** Callout box with gold left border */
export function CalloutBox({ title, children, icon }: { title?: string; children: React.ReactNode; icon?: string }) {
  return (
    <div className="border-l-4 border-gold/50 bg-gold/5 p-4 md:p-5 rounded-r my-4">
      {title && (
        <p className="font-semibold text-foreground mb-2 flex items-center gap-2">
          {icon && <span className="text-gold">{renderIcon(icon)}</span>}
          {title}
        </p>
      )}
      <div className="text-muted-foreground text-sm leading-relaxed">{children}</div>
    </div>
  );
}

/** Stat card for stat grids */
function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-card border border-border rounded p-4 text-center">
      <p className="text-xl md:text-2xl font-display font-semibold text-gold">{value}</p>
      <p className="text-xs md:text-sm text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

/** Grid of stat cards */
export function StatGrid({ stats }: { stats: { value: string; label: string }[] }) {
  return (
    <div className={cn("grid gap-3 my-4", stats.length === 2 ? "grid-cols-2" : stats.length >= 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-3")}>
      {stats.map((s, i) => (
        <StatCard key={i} value={s.value} label={s.label} />
      ))}
    </div>
  );
}

/** List with green check icons */
export function CheckList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2 my-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          <span className="text-muted-foreground">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** List with red X icons */
export function CrossList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2 my-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <span className="text-muted-foreground">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Calculation / example block */
export function CalculationBlock({ title, icon, children }: { title: string; icon?: string; children: React.ReactNode }) {
  return (
    <div className="bg-secondary/50 border border-border rounded p-4 md:p-5 my-4">
      <p className="font-semibold text-foreground mb-3 flex items-center gap-2">
        {icon && <span className="text-gold">{renderIcon(icon)}</span>}
        {title}
      </p>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-1">{children}</div>
    </div>
  );
}

/** Comparison block (2 sections stacked) */
export function ComparisonBlock({ blocks }: { blocks: { title: string; icon?: string; content: React.ReactNode; variant: "negative" | "positive" }[] }) {
  return (
    <div className="grid md:grid-cols-2 gap-3 my-4">
      {blocks.map((block, i) => (
        <div
          key={i}
          className={cn(
            "border rounded p-4",
            block.variant === "positive"
              ? "border-emerald-500/30 bg-emerald-500/5"
              : "border-red-500/20 bg-red-500/5"
          )}
        >
          <p className="font-semibold text-foreground mb-2 flex items-center gap-2 text-sm">
            {block.icon && <span className="text-gold">{renderIcon(block.icon)}</span>}
            {block.title}
          </p>
          <div className="text-sm text-muted-foreground">{block.content}</div>
        </div>
      ))}
    </div>
  );
}

/** Simple paragraph wrapper for FAQ answers */
export function P({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground leading-relaxed mb-3">{children}</p>;
}

/** Section title inside an answer */
export function AnswerTitle({ children, icon }: { children: React.ReactNode; icon?: string }) {
  return (
    <p className="font-semibold text-foreground mt-4 mb-2 flex items-center gap-2 text-sm">
      {icon && <span className="text-gold">{renderIcon(icon)}</span>}
      {children}
    </p>
  );
}

/** Numbered step */
export function Step({ number, children }: { number: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 mb-2">
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gold/20 text-gold text-xs font-bold flex items-center justify-center mt-0.5">
        {number}
      </span>
      <span className="text-sm text-muted-foreground">{children}</span>
    </div>
  );
}

/** Result line highlighted in green */
export function Result({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm font-semibold text-emerald-400 mt-2 p-2 bg-emerald-500/10 rounded">
      {children}
    </p>
  );
}
